import { z } from "zod";
import validator from "validator";

// One phone rule for every form. People type numbers many ways
// ("1 (780) 691-1778", "780.555.0123", "+44 20 7946 0958"), so we strip
// formatting, assume North America for 10 digits (or 11 starting with 1),
// and save the number as +<country><number>.
export const PHONE_EXAMPLE = "780 555 0123";
export const PHONE_ERROR =
  "Please enter a valid phone number, for example 780 555 0123. Outside Canada and the US, start with + and your country code.";

export function normalizePhone(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const compact = trimmed.replace(/[\s().\-‐-―]/g, "").replace(/^00/, "+");
  if (compact.startsWith("+")) return compact;
  if (/^\d{10}$/.test(compact)) return `+1${compact}`;
  if (/^1\d{10}$/.test(compact)) return `+${compact}`;
  return compact;
}

export function isValidPhone(value: string): boolean {
  const phone = normalizePhone(value);
  // North American numbers get the full check (area code and exchange rules);
  // elsewhere, any E.164 number (8 to 15 digits) is accepted, landlines included.
  if (phone.startsWith("+1")) return validator.isMobilePhone(phone, "en-CA", { strictMode: true }) || validator.isMobilePhone(phone, "en-US", { strictMode: true });
  return /^\+[2-9]\d{7,14}$/.test(phone);
}

/** Zod schema that accepts common formats and outputs +E.164. "phone" lets forms show an example. */
export function zodPhoneField(isOptional = false) {
  const inner = z.string().refine((v) => (isOptional && v === "") || isValidPhone(v), { message: PHONE_ERROR });
  return z.preprocess((v) => (typeof v === "string" ? normalizePhone(v) : v), inner).describe("phone");
}
